/**
 * ModelRunner — owns the assist worker, and is the only thing that talks to it.
 *
 * Its single job is correlation: turn a stream of messages on a Worker port into
 * ordinary promises, so that everything above it can await an embedding without
 * knowing a worker exists. It holds no opinion about what the vectors mean.
 *
 * Nothing is loaded until the first `warm()`. A photographer who never turns
 * Review Assist on never downloads a byte of the model.
 */

import { MODEL_TAG } from './assist-model';
import type { Embedder, EmbedderStatus, ModelProgress } from './embedder';

type Outgoing =
  | { type: 'ready'; device: 'webgpu' | 'wasm'; ms: number }
  | { type: 'embedding'; id: number; vector: Float32Array }
  | { type: 'progress'; loaded: number; total: number }
  | { type: 'error'; id?: number; message: string };

interface Pending {
  resolve(vector: Float32Array): void;
  reject(error: Error): void;
}

export class ModelRunner implements Embedder {
  readonly model = MODEL_TAG;

  private worker: Worker | null = null;
  private warming: Promise<EmbedderStatus> | null = null;
  private readonly pending = new Map<number, Pending>();
  private onProgress?: (p: ModelProgress) => void;
  private nextId = 1;
  private disposed = false;

  /** True when this browser can run the worker at all. */
  static get supported(): boolean {
    return typeof Worker !== 'undefined';
  }

  async warm(onProgress?: (p: ModelProgress) => void): Promise<EmbedderStatus> {
    if (this.disposed) throw new Error('Review Assist was stopped');
    this.onProgress = onProgress;

    this.warming ??= new Promise<EmbedderStatus>((resolve, reject) => {
      const worker = this.spawn();

      const settle = (event: MessageEvent<Outgoing>) => {
        const message = event.data;
        if (message.type === 'ready') {
          worker.removeEventListener('message', settle);
          resolve({ device: message.device, ms: message.ms });
        } else if (message.type === 'error' && message.id === undefined) {
          worker.removeEventListener('message', settle);
          // A failed load must not be cached as a permanent verdict: the usual
          // cause is a dropped connection during the download, and the obvious
          // thing a photographer does next is press the button again.
          this.warming = null;
          reject(new Error(message.message));
        }
      };

      worker.addEventListener('message', settle);
      worker.postMessage({ type: 'warm' });
    });

    return this.warming;
  }

  async embed(bytes: Uint8Array): Promise<Float32Array> {
    await this.warm(this.onProgress);
    const worker = this.spawn();
    const id = this.nextId++;

    return new Promise<Float32Array>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      // Copied rather than transferred. These are the same bytes the sharpness
      // pass just read, and detaching a buffer the caller still holds is the
      // kind of bug that only shows up under load.
      worker.postMessage({ type: 'embed', id, bytes: bytes.slice() });
    });
  }

  dispose(): void {
    this.disposed = true;
    this.rejectAll(new Error('Review Assist was stopped'));
    this.worker?.postMessage({ type: 'dispose' });
    this.worker?.terminate();
    this.worker = null;
    this.warming = null;
  }

  private spawn(): Worker {
    if (this.worker) return this.worker;

    const worker = new Worker(new URL('./assist.worker.ts', import.meta.url), { type: 'module' });
    worker.addEventListener('message', (event: MessageEvent<Outgoing>) => this.receive(event.data));
    worker.addEventListener('error', (event) => {
      // The worker itself died — a syntax error, an out-of-memory kill. Every
      // outstanding request is now unanswerable, so say so rather than hang.
      this.warming = null;
      this.rejectAll(new Error(event.message || 'the assist worker stopped'));
    });

    this.worker = worker;
    return worker;
  }

  private receive(message: Outgoing): void {
    if (message.type === 'progress') {
      this.onProgress?.({ loaded: message.loaded, total: message.total });
      return;
    }

    if (message.type === 'embedding') {
      this.pending.get(message.id)?.resolve(message.vector);
      this.pending.delete(message.id);
      return;
    }

    if (message.type === 'error' && message.id !== undefined) {
      this.pending.get(message.id)?.reject(new Error(message.message));
      this.pending.delete(message.id);
    }
  }

  private rejectAll(error: Error): void {
    for (const pending of this.pending.values()) pending.reject(error);
    this.pending.clear();
  }
}
